import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import notionService from '../services/notionService';

const FeedbackModal = ({ isOpen, onClose }) => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  
  const [feedbackForm, setFeedbackForm] = useState({
    feedbackType: 'general',
    priority: 'medium',
    content: '',
    browserInfo: '',
    deviceType: 'desktop'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Detect browser and device info
  React.useEffect(() => {
    const browserInfo = `${navigator.userAgent}`;
    const deviceType = /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop';
    
    setFeedbackForm(prev => ({
      ...prev,
      browserInfo,
      deviceType
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedbackForm.content.trim()) {
      alert(t('feedback.pleaseEnterContent'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Create feedback data
      const feedbackData = {
        submittedBy: userData?.displayName || currentUser?.email || t('feedback.unknownUser'),
        userEmail: currentUser?.email || '',
        userRole: userData?.role || 'unknown',
        feedbackType: feedbackForm.feedbackType,
        priority: feedbackForm.priority,
        feedbackContent: feedbackForm.content.trim(),
        status: 'New',
        submittedDate: new Date().toISOString(),
        userId: currentUser?.uid || '',
        browserInfo: feedbackForm.browserInfo,
        deviceType: feedbackForm.deviceType,
        appVersion: '1.0.0', // You can update this dynamically
        timestamp: new Date()
      };

      // Submit feedback using Notion service
      const result = await notionService.submitFeedback(feedbackData);
      
      if (result.success) {
        if (result.notionId) {
          // Successfully submitted to Notion
          alert(t('feedback.thankYouMessage'));
        } else if (result.storedLocally) {
          // Stored locally as fallback
          alert(t('feedback.thankYouMessage') + ' (Stored locally - Notion integration pending)');
        }
        
        // Reset form and close modal
        setFeedbackForm({
          feedbackType: 'general',
          priority: 'medium',
          content: '',
          browserInfo: feedbackForm.browserInfo,
          deviceType: feedbackForm.deviceType
        });
        onClose();
      } else {
        throw new Error(result.error || 'Failed to submit feedback');
      }
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert(t('feedback.submissionError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFeedbackForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-2xl rounded-2xl bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800">{t('feedback.title')}</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-blue-800">
              {t('feedback.helpMessage')}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('feedback.feedbackType')} *
              </label>
              <select
                name="feedbackType"
                value={feedbackForm.feedbackType}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
              >
                <option value="general">{t('feedback.types.general')}</option>
                <option value="bug">{t('feedback.types.bug')}</option>
                <option value="feature">{t('feedback.types.feature')}</option>
                <option value="uiux">{t('feedback.types.uiux')}</option>
                <option value="performance">{t('feedback.types.performance')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('feedback.priority')} *
              </label>
              <select
                name="priority"
                value={feedbackForm.priority}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
              >
                <option value="low">{t('feedback.priorities.low')}</option>
                <option value="medium">{t('feedback.priorities.medium')}</option>
                <option value="high">{t('feedback.priorities.high')}</option>
                <option value="critical">{t('feedback.priorities.critical')}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('feedback.content')} *
            </label>
            <textarea
              name="content"
              value={feedbackForm.content}
              onChange={handleInputChange}
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              placeholder={t('feedback.contentPlaceholder')}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('feedback.deviceType')}
              </label>
              <select
                name="deviceType"
                value={feedbackForm.deviceType}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="desktop">{t('feedback.devices.desktop')}</option>
                <option value="mobile">{t('feedback.devices.mobile')}</option>
                <option value="tablet">{t('feedback.devices.tablet')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('feedback.browserInfo')}
              </label>
              <input
                type="text"
                value={feedbackForm.browserInfo}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                readOnly
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('feedback.submitting') : t('feedback.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
